const COMMON_DOMAIN_MISTAKES = new Set([
  'gmail.con',
  'gmail.cim',
  'gmail.coom',
  'gmail.cm',
  'yandex.ry',
  'yandex.ty',
  'mail.ry',
  'mail.con',
]);

const PROVIDER_TLD_WHITELIST = {
  gmail: ['com'],
  googlemail: ['com'],
  yandex: ['ru', 'ua', 'by', 'kz', 'com'],
  ya: ['ru'],
  mail: ['ru'],
  bk: ['ru'],
  inbox: ['ru'],
  list: ['ru'],
  rambler: ['ru'],
  outlook: ['com', 'ru'],
  hotmail: ['com', 'ru'],
  live: ['com', 'ru'],
  yahoo: ['com', 'ru'],
  icloud: ['com'],
  me: ['com'],
};

const ALLOWED_TLD_PATTERN = /^[a-z]{2,24}$/;
const DOMAIN_SYMBOL_PATTERN = /^[a-z0-9.-]+$/;
const LOCAL_SYMBOL_PATTERN = /^[a-z0-9._+-]+$/i;

const POPULAR_DOMAINS = Object.entries(PROVIDER_TLD_WHITELIST).reduce(
  (acc, [provider, zones]) => {
    zones.forEach(zone => acc.push(`${provider}.${zone}`));
    return acc;
  },
  [],
);

const hasAllowedProviderZone = domain => {
  const parts = domain.split('.');
  if (parts.length !== 2) return true;

  const [provider, tld] = parts;
  const allowedTlds = PROVIDER_TLD_WHITELIST[provider];

  if (!allowedTlds) return true;

  return allowedTlds.includes(tld);
};

export const isEmailDomainValid = email => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const [, domain = ''] = email.trim().toLowerCase().split('@');

  if (!domain || domain.includes('..') || !DOMAIN_SYMBOL_PATTERN.test(domain)) {
    return false;
  }

  if (COMMON_DOMAIN_MISTAKES.has(domain)) {
    return false;
  }

  const domainParts = domain.split('.');

  if (domainParts.length < 2) {
    return false;
  }

  const tld = domainParts[domainParts.length - 1];

  if (!ALLOWED_TLD_PATTERN.test(tld)) {
    return false;
  }

  if (!hasAllowedProviderZone(domainParts.slice(-2).join('.'))) {
    return false;
  }

  return true;
};

export const isEmailLocalPartValid = local => {
  if (!local || typeof local !== 'string') return false;

  const value = local.trim();
  if (!value) return false;

  // avoid leading/trailing dot and consecutive dots
  if (value.startsWith('.') || value.endsWith('.') || value.includes('..')) {
    return false;
  }

  return LOCAL_SYMBOL_PATTERN.test(value);
};

export const isEmailValid = email => {
  if (!email || typeof email !== 'string') return false;

  const value = email.trim();
  if (!value) return false;

  const parts = value.split('@');
  if (parts.length !== 2) return false;

  const [local = '', domain = ''] = parts;
  if (!isEmailLocalPartValid(local)) return false;

  // domain validation uses full email string to keep existing behavior,
  // but also ensure the domain part is present.
  if (!domain) return false;

  return isEmailDomainValid(value);
};

const levenshtein = (a, b) => {
  const matrix = Array.from({length: a.length + 1}, (_, i) => [i]);

  for (let j = 0; j <= b.length; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      if (a[i - 1] === b[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + 1,
        );
      }
    }
  }

  return matrix[a.length][b.length];
};

export const getEmailDomainSuggestion = email => {
  if (!email || typeof email !== 'string') return null;

  const [local = '', domain = ''] = email.trim().toLowerCase().split('@');

  if (!local || !domain) return null;

  let bestMatch = null;

  POPULAR_DOMAINS.forEach(popularDomain => {
    const distance = levenshtein(domain, popularDomain);

    if (distance > 0 && distance <= 2) {
      if (!bestMatch || distance < bestMatch.distance) {
        bestMatch = {domain: popularDomain, distance};
      }
    }
  });

  return bestMatch ? `${local}@${bestMatch.domain}` : null;
};



